import { ButtonHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-metallic-900 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 dark:bg-vivid-purple dark:text-white dark:border-2 dark:border-vivid-cyan-bright dark:hover:bg-vivid-purple-medium focus:ring-primary-500',
      secondary:
        'bg-gray-600 text-white hover:bg-gray-700 dark:bg-vivid-blue-bright dark:text-white dark:border-2 dark:border-vivid-lime-bright dark:hover:bg-vivid-blue-dark focus:ring-gray-500',
      outline:
        'border-2 border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-vivid-cyan-bright dark:text-vivid-cyan-bright dark:hover:bg-vivid-cyan-bright/10 dark:hover:text-white focus:ring-primary-500',
      ghost: 'text-gray-700 hover:bg-gray-100 dark:text-vivid-lime-bright dark:hover:bg-metallic-800 dark:hover:text-vivid-cyan-bright focus:ring-gray-500',
      danger: 'bg-red-600 text-white hover:bg-red-700 dark:bg-vivid-pink-bright dark:text-white dark:border-2 dark:border-vivid-pink-light dark:hover:bg-vivid-pink-light focus:ring-red-500',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const widthClass = fullWidth ? 'w-full' : '';

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;

// Made with Bob
